import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Star, Phone, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon for contractors
const contractorIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    width: 36px;
    height: 36px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
    border: 3px solid white;
  ">
    <svg style="transform: rotate(45deg); width: 18px; height: 18px; color: white;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

// Featured contractor marker
const featuredIcon = new L.DivIcon({
  className: 'custom-marker-featured',
  html: `<div style="
    background: linear-gradient(135deg, #f59e0b, #d97706);
    width: 42px;
    height: 42px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(245, 158, 11, 0.5);
    border: 3px solid white;
  ">
    <svg style="transform: rotate(45deg); width: 20px; height: 20px; color: white;" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
    </svg>
  </div>`,
  iconSize: [42, 42],
  iconAnchor: [21, 42],
  popupAnchor: [0, -42],
});

// Map center updater component
const MapCenterUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

// City coordinates for Phoenix metro area
const CITY_COORDS = {
  'phoenix': { lat: 33.4484, lng: -112.0740 },
  'phoenix-az': { lat: 33.4484, lng: -112.0740 },
  'scottsdale': { lat: 33.4942, lng: -111.9261 },
  'scottsdale-az': { lat: 33.4942, lng: -111.9261 },
  'tempe': { lat: 33.4255, lng: -111.9400 },
  'tempe-az': { lat: 33.4255, lng: -111.9400 },
  'mesa': { lat: 33.4152, lng: -111.8315 },
  'mesa-az': { lat: 33.4152, lng: -111.8315 },
  'chandler': { lat: 33.3062, lng: -111.8413 },
  'chandler-az': { lat: 33.3062, lng: -111.8413 },
  'glendale': { lat: 33.5387, lng: -112.1860 },
  'glendale-az': { lat: 33.5387, lng: -112.1860 },
  'gilbert': { lat: 33.3528, lng: -111.7890 },
  'gilbert-az': { lat: 33.3528, lng: -111.7890 },
  'peoria': { lat: 33.5806, lng: -112.2374 },
  'peoria-az': { lat: 33.5806, lng: -112.2374 },
  'surprise': { lat: 33.6292, lng: -112.3680 },
  'surprise-az': { lat: 33.6292, lng: -112.3680 },
  'avondale': { lat: 33.4356, lng: -112.3496 },
  'avondale-az': { lat: 33.4356, lng: -112.3496 },
  'goodyear': { lat: 33.4353, lng: -112.3583 },
  'goodyear-az': { lat: 33.4353, lng: -112.3583 },
  'buckeye': { lat: 33.3703, lng: -112.5838 },
  'buckeye-az': { lat: 33.3703, lng: -112.5838 },
};

// Get coordinates for a contractor based on their location
const getContractorCoords = (contractor) => {
  const location = (contractor.location || contractor.city || 'phoenix').toLowerCase().replace(/,?\s*(az|arizona)?$/i, '').trim();
  const baseCoords = CITY_COORDS[location] || CITY_COORDS[`${location}-az`] || CITY_COORDS['phoenix'];
  
  // Add small random offset to prevent markers from stacking
  const offset = 0.01;
  return {
    lat: baseCoords.lat + (Math.random() - 0.5) * offset,
    lng: baseCoords.lng + (Math.random() - 0.5) * offset,
  };
};

const ContractorMap = ({ 
  contractors = [], 
  selectedCity = null,
  height = '400px',
  showSearch = true,
  className = ''
}) => {
  const navigate = useNavigate();
  const [mapCenter, setMapCenter] = useState([33.4484, -112.0740]); // Phoenix default
  const [zoom, setZoom] = useState(10);
  const [searchCity, setSearchCity] = useState('');

  // Update center when city changes
  useEffect(() => {
    if (selectedCity) {
      const cityKey = selectedCity.toLowerCase().replace(/,?\s*(az|arizona)?$/i, '').trim();
      const coords = CITY_COORDS[cityKey] || CITY_COORDS[`${cityKey}-az`];
      if (coords) {
        setMapCenter([coords.lat, coords.lng]);
        setZoom(12);
      }
    }
  }, [selectedCity]);

  // Handle city search
  const handleCitySearch = (city) => {
    const cityKey = city.toLowerCase().replace(/,?\s*(az|arizona)?$/i, '').trim();
    const coords = CITY_COORDS[cityKey] || CITY_COORDS[`${cityKey}-az`];
    if (coords) {
      setMapCenter([coords.lat, coords.lng]);
      setZoom(12);
    }
  };

  // Filter contractors with valid coordinates
  const contractorsWithCoords = contractors.map(contractor => ({
    ...contractor,
    coords: getContractorCoords(contractor)
  }));

  return (
    <div className={`relative ${className}`} data-testid="contractor-map">
      {/* City Search */}
      {showSearch && (
        <div className="absolute top-3 left-3 right-3 z-[1000] md:left-3 md:right-auto md:w-72">
          <div className="bg-white rounded-lg shadow-lg p-2">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search city..."
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCitySearch(searchCity);
                  }
                }}
                className="flex-1 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="map-search-input"
              />
              <button
                onClick={() => handleCitySearch(searchCity)}
                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                data-testid="map-search-btn"
              >
                <MapPin className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {['Phoenix', 'Scottsdale', 'Tempe', 'Mesa'].map(city => (
                <button
                  key={city}
                  onClick={() => {
                    setSearchCity(city);
                    handleCitySearch(city);
                  }}
                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                >
                  {city}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Map Legend */}
      <div className="absolute bottom-3 left-3 z-[1000] bg-white rounded-lg shadow-lg p-3 text-xs">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-4 h-4 rounded-full bg-gradient-to-br from-amber-400 to-amber-600"></div>
          <span>Featured Pro</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600"></div>
          <span>Contractor</span>
        </div>
      </div>

      {/* Map Container */}
      <MapContainer
        center={mapCenter}
        zoom={zoom}
        style={{ height, width: '100%', borderRadius: '0.75rem' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapCenterUpdater center={mapCenter} />
        
        {contractorsWithCoords.map((contractor) => (
          <Marker
            key={contractor.id}
            position={[contractor.coords.lat, contractor.coords.lng]}
            icon={contractor.is_featured ? featuredIcon : contractorIcon}
          >
            <Popup className="contractor-popup">
              <div className="p-1 min-w-[200px]">
                {/* Header */}
                <div className="flex items-start gap-3 mb-2">
                  <img
                    src={contractor.profile_photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(contractor.business_name || contractor.name)}&background=3b82f6&color=fff`}
                    alt={contractor.business_name || contractor.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-white shadow"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {contractor.business_name || contractor.name}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">
                      {contractor.categories?.[0] || contractor.service_type || 'General Contractor'}
                    </p>
                    {contractor.is_featured && (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium">
                        <Star className="w-3 h-3 fill-amber-400" /> Featured
                      </span>
                    )}
                  </div>
                </div>

                {/* Rating & Location */}
                <div className="flex items-center justify-between text-xs text-gray-600 mb-3">
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    <span>{contractor.rating?.toFixed(1) || '5.0'}</span>
                    <span className="text-gray-400">({contractor.reviews_count || 0})</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    <span>{contractor.location || contractor.city || 'Phoenix, AZ'}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/profile/${contractor.id}`)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    View Profile <ArrowRight className="w-3 h-3" />
                  </button>
                  {contractor.phone && (
                    <a
                      href={`tel:${contractor.phone}`}
                      className="flex items-center justify-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Phone className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* No contractors message */}
      {contractorsWithCoords.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 rounded-xl z-[500]">
          <div className="text-center p-6">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No contractors in this area</p>
            <p className="text-sm text-gray-500">Try searching a different city</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorMap;
